'use client';

import { Box, Button, Divider, Grid, Typography, useTheme } from '@mui/material';
import { IconCalendarTime, IconCurrencyDollar, IconPercentage } from '@tabler/icons-react';
import Link from 'next/link';

import { ExtendedLenderTier } from '@/lib/loanFilteringHelper';

type LoanCardProps = {
  tier: ExtendedLenderTier;
};

const formatCurrency = (num?: number | null): string | undefined => {
  if (typeof num !== 'number' || num <= 0) return undefined;

  if (num >= 1_000_000) {
    return `$${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
  } else if (num >= 1_000) {
    return `$${(num / 1_000).toFixed(0)}k`;
  } else {
    return `$${num}`;
  }
};

const formatTerm = (min?: number | null, max?: number | null): string => {
  if (!min || !max || max > 9000) return 'Quoted on application';
  return min === max ? `${min} months` : `${min} – ${max} months`;
};

const formatAPR = (min?: number | null, max?: number | null, hideApr?: boolean | null): string => {
  if (hideApr) return 'Quoted on application';
  if (!min || !max || (min === 0 && max === 0)) return 'Quoted on application';
  return min === max ? `${min.toFixed(2)}%` : `${min.toFixed(2)}% – ${max.toFixed(2)}%`;
};

export default function LoanCard({ tier }: LoanCardProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.common.white,
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 2.5 },
        mb: 2,
        minHeight: { xs: 'auto', sm: 320, md: 350 },
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md>
          <Grid container spacing={1.5} alignItems="center" justifyContent="center">
            {/* Logo */}
            <Grid item>
              {tier.lenderLogo ? (
                <Box
                  component="img"
                  src={tier.lenderLogo}
                  alt={tier.lenderName ?? 'Lender logo'}
                  sx={{ height: 100, width: 150, objectFit: 'contain' }}
                />
              ) : (
                <Typography variant="subtitle2" fontWeight={700} color={theme.palette.common.black}>
                  {tier.lenderName ?? 'Unknown Lender'}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              my: 2,
            }}
          >
            <Divider />
          </Box>

          <Grid
            container
            spacing={3}
            direction="column"
            alignItems={{ xs: 'center', md: 'flex-start' }}
            justifyContent={{ xs: 'center', md: 'flex-start' }}
          >
            {/* Interest */}
            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <IconPercentage size={18} stroke={1.8} />
                <Box textAlign={{ xs: 'center', md: 'left' }}>
                  <Typography variant="caption" color={theme.palette.common.black}>
                    Interest from
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color={theme.palette.common.black}
                  >
                    {formatAPR(tier.minApr, tier.maxApr, tier.hideApr)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Loan Amount */}
            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <IconCurrencyDollar size={18} stroke={1.8} />
                <Box textAlign={{ xs: 'center', md: 'left' }}>
                  <Typography variant="caption" color={theme.palette.common.black}>
                    Loan size
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color={theme.palette.common.black}
                  >
                    {(() => {
                      const min = formatCurrency(tier.minLoanAmount);
                      const max = formatCurrency(tier.maxLoanAmount);

                      if (min && max) return `${min} – ${max}`;
                      if (!min && max) return `Up to ${max}`;
                      if (min && !max) return `From ${min}`;
                      return 'Quoted on application';
                    })()}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Term */}
            <Grid item>
              <Box display="flex" alignItems="center" gap={1}>
                <IconCalendarTime size={18} stroke={1.8} />
                <Box textAlign={{ xs: 'center', md: 'left' }}>
                  <Typography variant="caption" color={theme.palette.common.black}>
                    Term length
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color={theme.palette.common.black}
                  >
                    {formatTerm(tier.minLoanTerm, tier.maxLoanTerm)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12} mt={2.5} textAlign="center" sx={{ order: { xs: -1, md: 0 } }}>
            <Link
              href={process.env.NEXT_PUBLIC_FULL_APP_URL || process.env.FULL_APP_URL || '#'}
              target="_blank"
              rel="noopener noreferrer"
              passHref
            >
              <Button
                component="a"
                fullWidth
                sx={{
                  borderRadius: 999,
                  backgroundColor: theme.palette.brand.primary,
                  color: 'white',
                  fontWeight: 600,
                  py: 1.1,
                  mb: 0.5,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.brand.primaryDark,
                  },
                }}
              >
                Compare now
              </Button>
            </Link>

            <Typography variant="label1" color={theme.palette.common.black}>
              on Lend.com.au
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
